import { StrictMode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  FluentProvider,
  teamsLightTheme,
  teamsDarkTheme,
  type Theme,
} from "@fluentui/react-components";
import { app } from "@microsoft/teams-js";
import { App } from "./App";
import { LanguageProvider } from "./i18n/LanguageContext";
import "./styles.css";

/** Wraps the app in a FluentProvider and syncs the theme with the Teams host. */
function Root() {
  const [theme, setTheme] = useState<Theme>(teamsLightTheme);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        await app.initialize();
        const context = await app.getContext();
        if (!mounted) return;
        setTheme(context.app.theme === "dark" ? teamsDarkTheme : teamsLightTheme);
        app.registerOnThemeChangeHandler((next) => {
          setTheme(next === "dark" ? teamsDarkTheme : teamsLightTheme);
        });
      } catch {
        // Not inside Teams — fall back to matching the OS color scheme.
        if (mounted && window.matchMedia?.("(prefers-color-scheme: dark)").matches) {
          setTheme(teamsDarkTheme);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <FluentProvider theme={theme} style={{ minHeight: "100vh" }}>
      <LanguageProvider>
        <App />
      </LanguageProvider>
    </FluentProvider>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
